// apps/web/src/features/crm/leads/components/LeadCreateModal.tsx
//
// Create-lead modal (#1081, #1363). Minimal raw-lead form: name, phone,
// email, comment. The backend supports an atomic clientId / newClient bind
// via the same endpoint, but the v1 web flow keeps create raw and exposes
// the bind step from the episode page's Client tab
// (`LeadClientBindingSection`) so the manager can decide after seeing
// duplicates.
//
// Unsaved-changes guard mirrors `ClientCreateModal`: local `dirty` boolean
// driven by `watch()` so the PhoneInput library's mount-time `+7` dial-code
// onChange does not flip dirty before user interaction.
//
// Duplicate handling (#1363): a colliding phone now hard-blocks at create.
// The backend returns a 409 `PHONE_ALREADY_EXISTS` whose PII-safe
// `meta.existing` points at the colliding lead/client by id + public code
// (NEVER a phone). The form renders an inline blocking error with an
// "Open existing" action that deep-links to that record and keeps the typed
// input intact. A successful create always closes the modal.
import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CreateLeadInputSchema, ErrorCode, type CreateLeadInput } from '@magimp/shared';
import { engagementsKeys, useCreateLead } from '@magimp/shared-hooks';
import {
  Button,
  EditorModal,
  FormRow,
  Input,
  PhoneInput,
  Textarea,
  useEditorGuard,
} from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { ApiClientError, errorMessage } from '../../../../api/errors.js';
import {
  phoneError,
  parsePhoneExistsMeta,
  type PhoneExistsExisting,
} from '../../../../shared/index.js';

export interface LeadCreateModalProps {
  onClose: () => void;
  onOpenExisting?: (leadId: string) => void;
}

const DEFAULTS: CreateLeadInput = {
  name: '',
  phone: null,
  email: null,
  comment: null,
  // WHY (#2307 D-6): empty string, not null — the lead title is non-nullable on
  //   the wire; the resolver normalizes an untouched '' to null, which onSubmit
  //   strips before send.
  title: '',
};

// WHY: `PhoneInput` uses `forceDialCode: true`, so it emits the country dial
// code (e.g. `+7`) at mount even when the field starts empty. The library
// only clears that placeholder on blur. For an OPTIONAL phone field, that
// pre-blur state would otherwise reach the Zod resolver as an invalid E.164
// string and block a name-only submit. Treat any `+<dial>` shape as "no
// phone entered" so the create flow accepts an unfilled phone slot.
function normalizeOptionalPhone(v: string): string | null {
  if (v === '') return null;
  if (/^\+\d{1,4}$/.test(v)) return null;
  return v;
}

export function LeadCreateModal({ onClose, onOpenExisting }: LeadCreateModalProps) {
  const { t } = useTranslation(['leads', 'common']);
  const create = useCreateLead();
  const [dirty, setDirty] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateLeadInput>({
    resolver: zodResolver(CreateLeadInputSchema),
    defaultValues: DEFAULTS,
  });

  // WHY: see [[feedback_phone_input_dirty_subscription]] — register watch
  // in a parent effect so PhoneInput's mount-time dial-code init never
  // sets dirty=true before user input.
  useEffect(() => {
    const subscription = watch(() => setDirty(true));
    return () => subscription.unsubscribe();
  }, [watch]);
  useEffect(() => () => setDirty(false), []);

  const resetDirty = useCallback(() => setDirty(false), []);

  return (
    <EditorModal
      open={true}
      title={t('leads:modals.createTitle')}
      width={520}
      dirty={dirty}
      busy={create.isPending}
      onConfirmClose={onClose}
    >
      <LeadCreateForm
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        create={create}
        resetDirty={resetDirty}
        onClose={onClose}
        {...(onOpenExisting ? { onOpenExisting } : {})}
      />
    </EditorModal>
  );
}

function LeadCreateForm({
  control,
  errors,
  handleSubmit,
  create,
  resetDirty,
  onClose,
  onOpenExisting,
}: {
  control: ReturnType<typeof useForm<CreateLeadInput>>['control'];
  errors: ReturnType<typeof useForm<CreateLeadInput>>['formState']['errors'];
  handleSubmit: ReturnType<typeof useForm<CreateLeadInput>>['handleSubmit'];
  create: ReturnType<typeof useCreateLead>;
  resetDirty: () => void;
  onClose: () => void;
  onOpenExisting?: (leadId: string) => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const show = useToastStore((s) => s.show);
  const { bypassNext, confirmDestructive } = useEditorGuard();
  const [phoneExists, setPhoneExists] = useState<PhoneExistsExisting | null>(null);

  const onSubmit = (values: CreateLeadInput) => {
    setPhoneExists(null);
    // WHY (#2307 D-6): the resolver normalizes an empty lead title to `null`, but
    //   the create wire's `title` is non-nullable (string | absent) — omit a null
    //   title rather than send null.
    const payload = { ...values };
    if (payload.title == null) delete payload.title;
    create.mutate(payload, {
      onSuccess: () => {
        resetDirty();
        // WHY (#2307 D-6): the shared create hook flushes only `leadsKeys`; the
        //   /leads list reads the Engagement projection, so flush it here or the
        //   new lead won't appear without a reload.
        void qc.invalidateQueries({ queryKey: engagementsKeys.all });
        show({ tone: 'success', text: t('leads:toasts.created') });
        onClose();
      },
      onError: (err) => {
        // #1363: a colliding phone hard-blocks. Surface the PII-safe
        //   `meta.existing` pointer inline with an open-existing action;
        //   keep the typed input intact. Any other error stays a toast.
        if (err instanceof ApiClientError && err.code === ErrorCode.PHONE_ALREADY_EXISTS) {
          const existing = parsePhoneExistsMeta(err.meta);
          if (existing) {
            setPhoneExists(existing);
            return;
          }
        }
        show({ tone: 'error', text: errorMessage(err) });
      },
    });
  };

  const onOpenExistingRecord = () => {
    if (!phoneExists) return;
    // WHY (#1363 F-R2-001): the form is still dirty here (the user typed
    // before hitting the 409), so `UnsavedChangesProvider`'s route blocker
    // would intercept a real navigation. `bypassNext()` is the one-shot
    // escape. But the blocker's same-location early-return does NOT consume
    // the bypass on a no-op nav (current === next location), so arming it
    // unconditionally would leak the armed flag to the NEXT real navigation
    // and silently skip the guard. Arm it ONLY when the target actually
    // changes the URL. The lead branch (#1810) navigates to the episode page
    // `/leads/<id>`, so it is a no-op iff the pathname already equals it; the
    // client branch is a no-op iff the pathname already equals `/clients/<id>`.
    if (phoneExists.type === 'lead') {
      const willNavigate = location.pathname !== `/leads/${phoneExists.id}`;
      if (willNavigate) bypassNext();
      // The lead opens on its episode page `/leads/<id>` (#1810). The page
      // caller wires that through `onOpenExisting`; fall back to a direct
      // navigate when the modal is used outside the leads workspace.
      if (onOpenExisting) onOpenExisting(phoneExists.id);
      else navigate(`/leads/${encodeURIComponent(phoneExists.id)}`);
    } else {
      const willNavigate = location.pathname !== `/clients/${phoneExists.id}`;
      if (willNavigate) bypassNext();
      navigate(`/clients/${encodeURIComponent(phoneExists.id)}`);
    }
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormRow label={t('leads:fields.title')} error={errors.title?.message}>
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <Input value={field.value ?? ''} onChange={field.onChange} invalid={!!errors.title} />
          )}
        />
      </FormRow>
      <FormRow label={t('leads:fields.name')} error={errors.name?.message}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input value={field.value ?? ''} onChange={field.onChange} invalid={!!errors.name} />
          )}
        />
      </FormRow>
      <FormRow
        label={t('leads:fields.phone')}
        error={phoneError(errors.phone?.message, t('leads:errors.invalidPhone'))}
      >
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <PhoneInput
              value={field.value ?? ''}
              onChange={(v) => field.onChange(normalizeOptionalPhone(v))}
              invalid={!!errors.phone}
              testId="lead-create-phone"
            />
          )}
        />
      </FormRow>
      <FormRow label={t('leads:fields.email')} error={errors.email?.message}>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v === '' ? null : v)}
              invalid={!!errors.email}
            />
          )}
        />
      </FormRow>
      <FormRow label={t('leads:fields.comment')} error={errors.comment?.message}>
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <Textarea
              value={field.value ?? ''}
              onChange={(v) => field.onChange(v === '' ? null : v)}
            />
          )}
        />
      </FormRow>
      {phoneExists && (
        <PhoneExistsBlock existing={phoneExists} onOpenExisting={onOpenExistingRecord} />
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" kind="primary" disabled={create.isPending}>
          {t('common:actions.create')}
        </Button>
      </div>
    </form>
  );
}

// Inline blocking region for a 409 PHONE_ALREADY_EXISTS collision (#1363).
// Shows the colliding record's public code when present (leads) and offers
// an open-existing action. NEVER renders a phone (PII-safe meta only).
function PhoneExistsBlock({
  existing,
  onOpenExisting,
}: {
  existing: PhoneExistsExisting;
  onOpenExisting: () => void;
}) {
  const { t } = useTranslation(['leads']);
  const actionLabel =
    existing.type === 'lead' ? t('leads:phoneExists.openLead') : t('leads:phoneExists.openClient');
  return (
    <div
      data-testid="lead-phone-exists"
      style={{
        border: '1px solid var(--status-error-fg)',
        background: 'var(--status-error-bg)',
        borderRadius: 'var(--r-1)',
        padding: 10,
        marginTop: 10,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {t('leads:phoneExists.title')}
      </div>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        {existing.publicCode
          ? t('leads:phoneExists.bodyWithCode', { code: existing.publicCode })
          : t('leads:phoneExists.body')}
      </div>
      <Button kind="primary" onClick={onOpenExisting} data-testid="lead-phone-exists-open">
        {actionLabel}
      </Button>
    </div>
  );
}
